import React, { useMemo, useState } from 'react';
import { X, Upload, FileSpreadsheet, Loader2, Check, Copy, AlertTriangle, GraduationCap, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const stripAccents = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function parseText(text) {
  const lines = text.split(/\r?\n/).map((l) => l.replace(/\u00a0/g, ' ')).filter((l) => l.trim());
  if (!lines.length) return [];
  const sep = lines[0].includes('\t') ? '\t' : lines[0].includes(';') ? ';' : ',';
  const table = lines.map((l) => l.split(sep).map((c) => c.trim().replace(/^"(.*)"$/, '$1')));
  const H = table[0].map(stripAccents);
  const prenomIdx = H.findIndex((h) => h.startsWith('prenom') || h.includes('first'));
  const nomIdx = H.findIndex((h, i) => i !== prenomIdx && (h.startsWith('nom') || h.includes('last')));
  const emailIdx = H.findIndex((h) => h.includes('mail'));
  const roleIdx = H.findIndex((h) => h.includes('role') || h.includes('type') || h.includes('profil'));
  const cohorteIdx = H.findIndex((h) => h.includes('cohorte') || h.includes('classe') || h.includes('groupe') || h.includes('etablissement'));
  const hasHeader = emailIdx >= 0 || (nomIdx >= 0 && prenomIdx >= 0);
  let rowsData;
  let cols;
  if (hasHeader) {
    rowsData = table.slice(1);
    cols = { nom: nomIdx, prenom: prenomIdx, email: emailIdx, role: roleIdx, cohorte: cohorteIdx };
  } else {
    // Sans en-tête : ordre attendu Nom ; Prénom ; Email ; [Rôle] ; [Cohorte]
    const eIdx = table[0].findIndex((c) => c.includes('@'));
    const emailCol = eIdx >= 0 ? eIdx : 2;
    cols = { nom: 0, prenom: 1, email: emailCol, role: emailCol + 1, cohorte: emailCol + 2 };
    rowsData = table;
  }
  const get = (r, i) => (i >= 0 && i < r.length ? (r[i] || '').trim() : '');
  return rowsData.map((r) => {
    const nom = get(r, cols.nom);
    const prenom = get(r, cols.prenom);
    const email = get(r, cols.email);
    const roleRaw = stripAccents(get(r, cols.role));
    const role = roleRaw.startsWith('form') || roleRaw.startsWith('resp') || roleRaw.includes('master') ? 'formateur' : 'apprenant';
    let warn = '';
    if (!`${prenom}${nom}`.trim()) warn = 'Nom manquant — ligne ignorée';
    else if (email && !EMAIL_RE.test(email)) warn = 'Email invalide';
    else if (!email) warn = 'Email manquant (pas d\u2019envoi possible)';
    return { holder_name: `${prenom} ${nom}`.trim(), email, role, cohorte: get(r, cols.cohorte), warn };
  });
}

function defaultExpires() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

export default function BulkImportDialog({ token, onClose, onDone }) {
  const [raw, setRaw] = useState('');
  const [expires, setExpires] = useState(defaultExpires());
  const [starts, setStarts] = useState('');
  const [sendEmails, setSendEmails] = useState(false);
  const [establishment, setEstablishment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const rows = useMemo(() => parseText(raw), [raw]);
  const validRows = rows.filter((r) => r.holder_name);
  const formateurs = validRows.filter((r) => r.role === 'formateur').length;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      if (/\.xlsx?$/i.test(file.name)) {
        const XLSX = await import('xlsx');
        const wb = XLSX.read(await file.arrayBuffer());
        setRaw(XLSX.utils.sheet_to_csv(wb.Sheets[wb.SheetNames[0]], { FS: ';' }));
      } else {
        setRaw(await file.text());
      }
      // Cohorte dans le nom du fichier (demande user) : pré-remplit l'établissement.
      setEstablishment((prev) => prev || file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim());
    } catch {
      setError('Impossible de lire ce fichier.');
    }
    e.target.value = '';
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/licenses/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          rows: validRows.map(({ holder_name, email, role, cohorte }) => ({
            holder_name, email: EMAIL_RE.test(email) ? email : '', role, cohorte,
          })),
          expires_at: new Date(`${expires}T23:59:59`).toISOString(),
          starts_at: starts ? new Date(starts).toISOString() : null,
          send_emails: sendEmails,
          establishment,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setResult(data);
    } catch (e) {
      setError(String(e.message || e));
    }
    setSubmitting(false);
  };

  const copyKeys = () => {
    const txt = (result?.licenses || []).map((l) => `${l.holder_name}\t${l.license_key}\t${l.role}`).join('\n');
    navigator.clipboard?.writeText(txt)?.catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="bulk-import-dialog">
      <div className="bg-ink-800 border border-ink-600 rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-600 sticky top-0 bg-ink-800 z-10">
          <h2 className="font-bold text-foreground flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-accent" /> Import en masse de licences
          </h2>
          <button aria-label="Fermer" onClick={onClose} className="p-1.5 text-slate-500 hover:text-foreground rounded" data-testid="bulk-import-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {result ? (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-money font-bold" data-testid="bulk-import-success">
              <Check className="w-5 h-5" /> {result.created} licences créées
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {[
                { label: 'Formateurs', v: result.formateurs },
                { label: 'Apprenants', v: result.apprenants },
                { label: 'Rattachés à une cohorte', v: result.linked },
                ...(result.co_responsables ? [{ label: 'Co-responsables', v: result.co_responsables }] : []),
                { label: 'Emails envoyés', v: result.emails_sent },
              ].map((s) => (
                <div key={s.label} className="bg-background border border-ink-600 rounded-lg py-3">
                  <div className="text-xl font-black text-accent">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
            {(result.errors || []).length > 0 && (
              <div className="bg-warn/10 border border-warn/40 rounded-lg p-3 text-xs text-warn space-y-1" data-testid="bulk-import-warnings">
                {result.errors.map((e, i) => <p key={i}>⚠ {e}</p>)}
              </div>
            )}
            <div className="bg-background border border-ink-600 rounded-lg max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody>
                  {(result.licenses || []).map((l, i) => (
                    <tr key={i} className="border-b border-ink-600/50">
                      <td className="px-3 py-1.5 text-slate-300">{l.holder_name}</td>
                      <td className="px-3 py-1.5"><code className="ph-no-capture font-mono text-accent">{l.license_key}</code></td>
                      <td className="px-3 py-1.5 text-slate-500">{l.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={copyKeys} className="flex items-center gap-1.5 px-4 py-2 border border-ink-600 rounded-lg text-sm text-slate-300 hover:bg-ink-700/40" data-testid="bulk-import-copy-keys">
                {copied ? <Check className="w-4 h-4 text-money" /> : <Copy className="w-4 h-4" />} Copier les clés
              </button>
              <button onClick={onDone} className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-bold hover:bg-accent-hover" data-testid="bulk-import-done">
                Terminer
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <p className="text-xs text-slate-400">
              Collez votre liste (copiée depuis Excel) ou importez un fichier <strong>CSV / Excel</strong>.
              Colonnes reconnues : <code className="text-accent">Nom · Prénom · Email · Rôle (apprenant/formateur) · Cohorte</code>.
              Sans en-tête, l'ordre attendu est Nom ; Prénom ; Email ; Rôle ; Cohorte.
              Plusieurs formateurs sur la même cohorte : le 1ᵉʳ devient responsable principal, les suivants co-responsables.
            </p>

            <div className="flex items-start gap-3 flex-wrap">
              <textarea
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={"Nom\tPrénom\tEmail\tRôle\tCohorte\nDupont\tMarie\tmarie.dupont@lycee.fr\tapprenant\tBTS GTLA 1\n…"}
                className="ph-no-capture flex-1 min-w-[300px] h-32 bg-background border border-ink-600 rounded-lg p-3 text-xs font-mono text-foreground placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-accent"
                data-testid="bulk-import-textarea"
              />
              <label className="flex flex-col items-center justify-center gap-2 w-40 h-32 border-2 border-dashed border-ink-600 rounded-lg cursor-pointer hover:border-accent/60 hover:bg-accent/5 transition-colors text-slate-500 hover:text-accent">
                <Upload className="w-6 h-6" />
                <span className="text-[10px] uppercase tracking-wider font-bold text-center px-2">Fichier CSV / Excel</span>
                <input type="file" accept=".csv,.tsv,.txt,.xls,.xlsx" className="hidden" onChange={onFile} data-testid="bulk-import-file" />
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Expiration *</label>
                <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)}
                  className="w-full bg-background border border-ink-600 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                  data-testid="bulk-import-expires" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Début de session (salle d'attente)</label>
                <input type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)}
                  className="w-full bg-background border border-ink-600 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                  data-testid="bulk-import-starts" />
                <p className="text-[10px] text-slate-600 mt-0.5">Optionnel — les apprenants patientent jusqu'à cette date.</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Établissement / cohorte par défaut</label>
                <input type="text" value={establishment} onChange={(e) => setEstablishment(e.target.value)} placeholder="Lycée Jean Moulin"
                  className="w-full bg-background border border-ink-600 rounded-lg px-3 py-2 text-xs text-foreground placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-accent"
                  data-testid="bulk-import-establishment" />
              </div>
              <label className="flex items-center gap-2 mt-5 cursor-pointer select-none">
                <input type="checkbox" checked={sendEmails} onChange={(e) => setSendEmails(e.target.checked)}
                  className="w-4 h-4 accent-[--accent]" data-testid="bulk-import-send-emails" />
                <span className="text-xs text-slate-300">Envoyer les emails de bienvenue</span>
              </label>
            </div>

            {rows.length > 0 && (
              <div className="bg-background border border-ink-600 rounded-lg max-h-64 overflow-y-auto" data-testid="bulk-import-preview">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-ink-700/90">
                    <tr>
                      {['#', 'Nom complet', 'Email', 'Rôle', 'Cohorte', ''].map((h) => (
                        <th key={h} className="text-left px-3 py-2 text-[10px] uppercase text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={`border-b border-ink-600/50 ${r.warn.startsWith('Nom') ? 'opacity-40' : ''}`}>
                        <td className="px-3 py-1.5 text-slate-600">{i + 1}</td>
                        <td className="px-3 py-1.5 text-slate-200 font-medium">{r.holder_name || '—'}</td>
                        <td className="ph-no-capture px-3 py-1.5 text-slate-400">{r.email || '—'}</td>
                        <td className="px-3 py-1.5">
                          {r.role === 'formateur' ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 font-bold inline-flex items-center gap-1">
                              <GraduationCap className="w-3 h-3" /> Formateur
                            </span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent/15 text-accent font-bold inline-flex items-center gap-1">
                              <User className="w-3 h-3" /> Apprenant
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-slate-400">{r.cohorte || '—'}</td>
                        <td className="px-3 py-1.5">
                          {r.warn && <span className="text-[10px] text-warn flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {r.warn}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 bg-danger/10 border border-danger/40 rounded-lg px-4 py-2.5 text-sm text-danger" data-testid="bulk-import-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500" data-testid="bulk-import-count">
                {validRows.length} licence{validRows.length > 1 ? 's' : ''} à créer
                {formateurs > 0 && ` — dont ${formateurs} formateur${formateurs > 1 ? 's' : ''}`}
              </p>
              <button
                onClick={submit}
                disabled={submitting || validRows.length === 0 || !expires}
                className="flex items-center gap-2 bg-accent text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="bulk-import-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Créer {validRows.length} licence{validRows.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
